import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    topics: '',
    detailedContent: '',
    keyPoints: ''
  });

  const categories = [
    'Communication',
    'Digital Safety',
    'Discipline',
    'Emotional Intelligence',
    'Single Parenting',
    'Blended Families',
    'Mental Health'
  ];

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (resource) => {
    setEditingResource(resource);
    
    // Format keyPoints - handle both string array and object array formats
    let keyPointsStr = '';
    if (resource.keyPoints && resource.keyPoints.length > 0) {
      keyPointsStr = resource.keyPoints.map(kp => {
        if (typeof kp === 'string') return kp;
        if (kp.point) return `${kp.point}|||${kp.explanation || ''}`;
        return '';
      }).join('\n');
    }

    setFormData({
      category: resource.category,
      title: resource.title,
      description: resource.description,
      topics: resource.topics?.join('\n') || '',
      detailedContent: resource.detailedContent || '',
      keyPoints: keyPointsStr
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingResource(null);
    setFormData({
      category: '',
      title: '',
      description: '',
      topics: '',
      detailedContent: '',
      keyPoints: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Parse keyPoints - support both simple and detailed format
    const keyPointsArray = formData.keyPoints.split('\n')
      .filter(kp => kp.trim())
      .map(kp => {
        if (kp.includes('|||')) {
          const [point, explanation] = kp.split('|||');
          return { point: point.trim(), explanation: explanation?.trim() || '' };
        }
        return kp.trim();
      });

    const resourceData = {
      category: formData.category,
      title: formData.title,
      description: formData.description,
      topics: formData.topics.split('\n').filter(t => t.trim()),
      detailedContent: formData.detailedContent,
      keyPoints: keyPointsArray
    };

    try {
      if (editingResource) {
        await axios.put(`${API}/admin/resources/${editingResource.id}`, resourceData);
      } else {
        await axios.post(`${API}/admin/resources`, resourceData);
      }
      setShowModal(false);
      fetchResources();
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Failed to save resource. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      await axios.delete(`${API}/admin/resources/${resourceId}`);
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource.');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Resources</h1>
            <p className="text-gray-600">Manage parenting resources ({resources.length} total)</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-teal-500 to-green-500 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Resources Table */}
        <Card className="border-2">
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold w-8"></th>
                  <th className="text-left p-4 font-semibold">Title</th>
                  <th className="text-left p-4 font-semibold">Category</th>
                  <th className="text-left p-4 font-semibold">Topics</th>
                  <th className="text-right p-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredResources.map((resource) => (
                  <React.Fragment key={resource.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <button
                          onClick={() => setExpandedRow(expandedRow === resource.id ? null : resource.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {expandedRow === resource.id ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                          }
                        </button>
                      </td>
                      <td className="p-4">
                        <span className="font-medium">{resource.title}</span>
                        <p className="text-sm text-gray-500 truncate max-w-md">{resource.description}</p>
                      </td>
                      <td className="p-4">
                        <Badge className="bg-teal-100 text-teal-800">{resource.category}</Badge>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600">{resource.topics?.length || 0} topics</span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(resource)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(resource.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === resource.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="p-4">
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-sm mb-1">Topics:</h4>
                              <div className="flex flex-wrap gap-2">
                                {resource.topics?.map((topic, i) => (
                                  <Badge key={i} variant="outline">{topic}</Badge>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm mb-1">Key Points ({resource.keyPoints?.length || 0}):</h4>
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                {resource.keyPoints?.slice(0, 3).map((kp, i) => (
                                  <li key={i}>{typeof kp === 'string' ? kp : kp.point}</li>
                                ))}
                                {resource.keyPoints?.length > 3 && (
                                  <li className="text-gray-400">...and {resource.keyPoints.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                <h2 className="text-2xl font-bold">
                  {editingResource ? 'Edit Resource' : 'Create Resource'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full p-2 border rounded-md"
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={2}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Topics (one per line) *</label>
                  <textarea
                    value={formData.topics}
                    onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={3}
                    placeholder="Death and loss&#10;Divorce&#10;Bullying"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Detailed Content</label>
                  <textarea
                    value={formData.detailedContent}
                    onChange={(e) => setFormData({ ...formData, detailedContent: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Key Points (one per line, use ||| to separate point from explanation)
                  </label>
                  <textarea
                    value={formData.keyPoints}
                    onChange={(e) => setFormData({ ...formData, keyPoints: e.target.value })}
                    className="w-full p-2 border rounded-md font-mono text-sm"
                    rows={6}
                    placeholder="Create safe space|||When kids share something difficult, resist the urge to react with shock&#10;Listen more than talk|||Let them finish completely before responding"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: "Key point|||Detailed explanation" or just "Key point" for simple entries
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 text-white" disabled={saving}>
                    {saving ? 'Saving...' : (editingResource ? 'Update Resource' : 'Create Resource')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminResources;
